import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useBrand } from "../lib/brand";
import { useGenerations } from "../lib/generations";
import { Plus, Search, Trash2, X, MessageSquare, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";

/**
 * Right-side collapsible drawer with chats for the current brand.
 * Mirrors the reference design: "+ Nuevo Chat" button, search field,
 * list of chats by date.
 *
 * Reusable across the image studio and Copy Studio: pass `basePath`
 * (`/chats` or `/copy`), `apiPrefix` (`/chats` or `/copy/chats`), and
 * `resetEvent` (the custom event the parent listens to so the composer
 * resets when "Nuevo chat" is clicked from the same route).
 */
export default function ChatsDrawer({
    activeChatId,
    open,
    onClose,
    basePath = "/chats",
    apiPrefix = "/chats",
    resetEvent = "kc:new-chat-requested",
    allowDeleteAlways = false,
}) {
    const { currentBrandId } = useBrand();
    const { isChatGenerating } = useGenerations();
    const [chats, setChats] = useState([]);
    const [q, setQ] = useState("");
    // Tracks whether the chat list is being (re)loaded for a different
    // brand than the one we previously rendered. We DON'T flip this on
    // every background poll (every 25s) — that would flash a spinner
    // for no reason. Only the brand-change path swaps to the loading
    // state so the user gets clear visual feedback that the historial
    // is being repopulated for the newly selected brand.
    const [isLoading, setIsLoading] = useState(true);
    // Remember which brand the current `chats` array belongs to. When
    // `currentBrandId` differs from this, we know we're in transition
    // and must show the skeleton (even before the network call lands).
    const loadedBrandRef = useRef(null);
    const navigate = useNavigate();
    const confirm = useConfirm();

    const load = (showSpinner = false) => {
        if (showSpinner) setIsLoading(true);
        const targetBrand = currentBrandId;
        api.get(apiPrefix)
            .then((r) => {
                // Guard against out-of-order responses: if the user switched
                // brands while this fetch was in flight, a stale poll for
                // the previous brand can land AFTER the new-brand fetch
                // and overwrite the list with the wrong data. Drop any
                // response whose brand no longer matches.
                if (targetBrand !== currentBrandId) return;
                setChats(r.data);
                loadedBrandRef.current = targetBrand;
            })
            .catch(() => {})
            .finally(() => {
                if (targetBrand === currentBrandId) setIsLoading(false);
            });
    };

    // Brand changed (or first mount): clear the list immediately so the
    // user never sees the previous brand's chats sitting under a spinner,
    // then fetch with the loading flag on.
    useEffect(() => {
        if (loadedBrandRef.current !== currentBrandId) {
            setChats([]);
            setIsLoading(true);
        }
        load(loadedBrandRef.current !== currentBrandId);
        const t = setInterval(() => load(false), 25_000);
        const onBrand = () => {
            setChats([]);
            setIsLoading(true);
            load(true);
        };
        const onChatsUpdated = () => load(false);
        window.addEventListener("kc:brand-changed", onBrand);
        window.addEventListener("kc:chats-updated", onChatsUpdated);
        return () => {
            clearInterval(t);
            window.removeEventListener("kc:brand-changed", onBrand);
            window.removeEventListener("kc:chats-updated", onChatsUpdated);
        };
    }, [currentBrandId, apiPrefix]);

    const newChat = () => {
        // Lazy creation everywhere — the row is inserted on first send.
        window.dispatchEvent(new CustomEvent(resetEvent));
        navigate(basePath);
    };

    const del = async (id, ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const ok = await confirm({
            title: "¿Eliminar este chat?",
            description: "Se eliminará permanentemente, incluyendo los mensajes asociados.",
            confirmLabel: "Sí, eliminar",
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        try {
            await api.delete(`${apiPrefix}/${id}`);
            toast.success("Chat eliminado");
            if (id === activeChatId) navigate(basePath);
            load(false);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "No se pudo eliminar el chat",
            );
        }
    };

    const filtered = chats.filter((c) =>
        c.title?.toLowerCase().includes(q.toLowerCase()),
    );

    if (!open) return null;

    return (
        <aside
            data-testid="chats-drawer"
            className="w-[320px] shrink-0 border-l hairline border-ink-600 bg-ink-900/40 flex flex-col min-h-0 h-full"
        >
            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
                <div>
                    <h2 className="font-display text-xl text-fog-100">
                        Historial de Chats
                    </h2>
                    <p className="font-mono text-[10px] tracking-[0.25em] text-fog-400 uppercase mt-0.5">
                        Marca actual
                    </p>
                </div>
                <button
                    data-testid="chats-drawer-close-btn"
                    onClick={onClose}
                    aria-label="Cerrar"
                    className="text-fog-400 hover:text-periwinkle-800 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="px-5 pb-3 space-y-3 shrink-0">
                <button
                    data-testid="chats-drawer-new-btn"
                    onClick={newChat}
                    className="w-full rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-on font-medium px-4 py-3 flex items-center justify-center gap-2 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Nuevo chat
                </button>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fog-400" />
                    <input
                        data-testid="chats-drawer-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar chat…"
                        className="w-full rounded-xl bg-ink-900/60 border hairline border-ink-600 text-fog-100 text-sm pl-9 pr-3 py-2.5 outline-none focus:border-periwinkle-500 placeholder:text-fog-500"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 px-3 pb-4 space-y-1.5">
                {isLoading && (
                    <div
                        data-testid="chats-drawer-loading"
                        className="px-3 py-8 flex flex-col items-center justify-center gap-3 text-fog-400"
                    >
                        <Loader2 className="w-5 h-5 animate-spin text-periwinkle-800" />
                        <p className="font-mono text-[10px] tracking-[0.25em] uppercase">
                            Cargando chats…
                        </p>
                        <div className="w-full space-y-2 mt-2" aria-hidden="true">
                            {[0, 1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="h-12 rounded-xl border hairline border-ink-600 bg-ink-800/40 overflow-hidden relative"
                                >
                                    <div
                                        className="absolute inset-0 opacity-40 animate-shimmer"
                                        style={{
                                            background:
                                                "linear-gradient(90deg, transparent, rgba(165,166,255,0.15), transparent)",
                                            backgroundSize: "200% 100%",
                                            animationDelay: `${i * 120}ms`,
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {!isLoading && filtered.length === 0 && (
                    <p className="px-3 py-6 text-center text-xs text-fog-500 font-mono">
                        {chats.length === 0
                            ? "Aún no tienes chats."
                            : "Sin resultados."}
                    </p>
                )}
                {!isLoading && filtered.map((c) => {
                    const active = c.id === activeChatId;
                    const canDelete = allowDeleteAlways || (c.message_count || 0) === 0;
                    const generating = isChatGenerating(c.id);
                    return (
                        <button
                            key={c.id}
                            data-testid={`chats-drawer-item-${c.id}`}
                            onClick={() => navigate(`${basePath}/${c.id}`)}
                            className={`group w-full text-left px-3 py-2.5 rounded-xl border hairline flex items-start gap-3 transition-colors ${
                                active
                                    ? "bg-periwinkle-500/10 border-periwinkle-500/50"
                                    : generating
                                      ? "border-periwinkle-500/40 bg-periwinkle-500/5"
                                      : "border-ink-600 hover:bg-ink-800/60"
                            }`}
                        >
                            {generating ? (
                                <span
                                    data-testid={`chats-drawer-generating-${c.id}`}
                                    className="mt-1 shrink-0 inline-flex items-center justify-center w-3.5 h-3.5 text-periwinkle-800"
                                    title="Hay una generación en curso en este chat"
                                >
                                    <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                                </span>
                            ) : (
                                <MessageSquare
                                    className={`w-3.5 h-3.5 mt-1 shrink-0 ${
                                        active ? "text-periwinkle-800" : "text-fog-400"
                                    }`}
                                />
                            )}
                            <span className="flex-1 min-w-0">
                                <span className="block text-sm text-fog-100 truncate">
                                    {c.title}
                                </span>
                                {generating ? (
                                    <span className="mt-0.5 inline-flex items-center gap-1.5 font-mono text-[9px] tracking-widest uppercase text-periwinkle-800">
                                        <span className="inline-block w-1 h-1 rounded-full bg-periwinkle-400 animate-pulse" />
                                        Generando…
                                    </span>
                                ) : (
                                    <span className="block font-mono text-[10px] text-fog-500 mt-0.5">
                                        {new Date(c.updated_at).toLocaleDateString("es-CO", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                        })}
                                    </span>
                                )}
                            </span>
                            {canDelete ? (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    data-testid={`chats-drawer-delete-${c.id}`}
                                    onClick={(ev) => del(c.id, ev)}
                                    onKeyDown={(ev) => {
                                        if (ev.key === "Enter" || ev.key === " ")
                                            del(c.id, ev);
                                    }}
                                    aria-label="Eliminar chat vacío"
                                    title="Eliminar chat vacío"
                                    className="text-fog-500 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity self-center cursor-pointer inline-flex items-center justify-center w-6 h-6 rounded"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </span>
                            ) : null}
                        </button>
                    );
                })}
            </div>
        </aside>
    );
}
